import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@shared/context/AuthContext';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { styles } from './AuthGuard.styles';

export interface AuthGuardProps {
  children: ReactNode;
}

/**
 * ProtectedRoute component that guards routes requiring authentication.
 * Redirects to login page if user is not authenticated.
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Component to display while checking authentication status.
 */
export const AuthCheckingFallback: React.FC = () => {
  const { t } = useTranslation('auth');
  return (
    <div style={styles.fallbackContainer}>
      <LoadingSpinner />
      <p style={styles.text}>{t('accessibility.checkingAuth')}</p>
    </div>
  );
};

/**
 * Public route guard that redirects authenticated users away from auth pages.
 */
export interface PublicGuardProps {
  children: ReactNode;
  redirectPath?: string;
}

export const PublicGuard: React.FC<PublicGuardProps> = ({
  children,
  redirectPath = '/dashboard',
}) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};
